import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import secureLocalStorage from "react-secure-storage";
import { User, UserSettings, UserRole } from './services/user.service';
import UserService from './services/user.service';
import {
    applyAudioSettingsPatch,
    normalizeAudioSettingLevel0to100,
    type AudioSettingsPatch,
} from './utils/applyAudioSettingsPatch';
import {
    setAuthToken,
    getAuthToken,
    removeAuthToken,
    parseJwtPayload,
    isJwtExpired,
    isGuestJwtToken,
} from './utils/authToken';
import Loading from './components/loading';

/**
 * Auth user – single identity type for both logged-in users and guests.
 * Use userId (number for user, string for guest) + guest to determine identity.
 */
export interface AuthUser {
    userId: number | string;
    guest: boolean;
    username: string;
    email: string | null;
    role: UserRole;
    mmr: number;
    settings: UserSettings | null;
}

// Context type
interface AuthContextType {
    user: AuthUser;
    storeUser: (newUser: Partial<AuthUser>) => void;
    /** Map backend User from login/register to AuthUser and store */
    setLoggedInUser: (user: User) => void;
    /** Clear auth and immediately obtain a new guest session so user always has an identity */
    logout: () => Promise<void>;
    /**
     * Update music/sfx in memory + localStorage. Guests: client only.
     * Registered users: optimistic local update, then PUT /api/users/me; reverts on failure.
     */
    patchUserSettings: (patch: AudioSettingsPatch) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

/** Placeholder guest user used only as initial state before guest-session is obtained */
function createPlaceholderGuest(): AuthUser {
    return {
        userId: '',
        username: '…',
        email: null,
        role: 'USER',
        mmr: 1000,
        guest: true,
        settings: null,
    };
}

/** Build AuthUser from backend guest-session response (token stored separately) */
function authUserFromGuestSession(
    res: { id: string; username: string; isGuest: true },
    /** When refreshing token, keep client-only prefs (e.g. music/sfx) from the previous guest row */
    preserveSettingsFrom?: AuthUser | null
): AuthUser {
    return {
        userId: res.id,
        username: res.username,
        email: null,
        role: 'USER',
        mmr: 1000,
        guest: true,
        settings: preserveSettingsFrom?.settings ?? null,
    };
}

/** Map backend User (from login/register) to AuthUser */
function authUserFromUser(backendUser: User): AuthUser {
    return {
        userId: backendUser.id,
        username: backendUser.username,
        email: backendUser.email,
        role: backendUser.role,
        mmr: backendUser.mmr,
        guest: false,
        settings: backendUser.settings,
    };
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<AuthUser>(createPlaceholderGuest);
    const [loading, setLoading] = useState(true);
    const userRef = useRef(user);
    userRef.current = user;

    function storeUser(newUser: Partial<AuthUser>) {
        const userToStore: AuthUser = {
            ...user,
            ...newUser,
            userId: newUser.userId ?? user.userId,
            role: newUser.role ?? user.role ?? 'USER',
        };
        setUser(userToStore);
        secureLocalStorage.setItem('user', userToStore);
    }

    function setLoggedInUser(backendUser: User) {
        storeUser(authUserFromUser(backendUser));
    }

    async function patchUserSettings(patch: AudioSettingsPatch) {
        if (patch.music === undefined && patch.sfx === undefined) {
            return;
        }
        const u = userRef.current;
        const prevSettings = u.settings;
        const nextSettings = applyAudioSettingsPatch(u, patch);
        storeUser({ settings: nextSettings });

        if (u.guest) {
            return;
        }

        try {
            const body: { music?: number; sfx?: number } = {};
            if (patch.music !== undefined) body.music = normalizeAudioSettingLevel0to100(patch.music);
            if (patch.sfx !== undefined) body.sfx = normalizeAudioSettingLevel0to100(patch.sfx);
            const { data } = await UserService.update(body);
            setLoggedInUser(data);
        } catch (e) {
            storeUser({ settings: prevSettings });
            console.error(e);
            throw e;
        }
    }

    /** Obtain guest session from backend and store token + guest user */
    async function obtainGuestSession() {
        const { data } = await UserService.createGuestSession();
        setAuthToken(data.token);
        const guestUser = authUserFromGuestSession(data);
        storeUser(guestUser);
    }

    /** Clear auth and immediately obtain a new guest session so user always has an identity */
    async function logout() {
        removeAuthToken();
        secureLocalStorage.removeItem('user');
        await obtainGuestSession();
    }

    useEffect(() => {
        let cancelled = false;

        function normalizeStoredUser(u: AuthUser): AuthUser {
            return {
                ...createPlaceholderGuest(),
                ...u,
                role: u.role ?? 'USER',
            };
        }

        /**
         * Session bootstrap (see docs/guest_auth_session_implementation_guide.md):
         * - Registered user: require non-expired JWT or redirect to login (no user refresh API yet).
         * - Guest: refresh expired guest JWT via POST /auth/guest-session/refresh when possible;
         *   otherwise create a new guest (new guestId).
         */
        async function init() {
            try {
                const token = getAuthToken();
                const stored = secureLocalStorage.getItem('user') as AuthUser | null;

                // --- Registered user (not guest) ---
                if (stored && !stored.guest) {
                    if (!token || isJwtExpired(token)) {
                        removeAuthToken();
                        secureLocalStorage.removeItem('user');
                        if (!cancelled) {
                            window.location.href = '/login';
                        }
                        return;
                    }
                    if (!cancelled) setUser(normalizeStoredUser(stored));
                    return;
                }

                // --- Guest with stored profile ---
                if (stored?.guest) {
                    if (!token) {
                        await obtainGuestSession();
                        return;
                    }

                    if (isGuestJwtToken(token)) {
                        if (isJwtExpired(token)) {
                            try {
                                const { data } = await UserService.refreshGuestSession(token);
                                if (cancelled) return;
                                setAuthToken(data.token);
                                storeUser(authUserFromGuestSession(data, stored));
                            } catch {
                                if (cancelled) return;
                                removeAuthToken();
                                secureLocalStorage.removeItem('user');
                                await obtainGuestSession();
                            }
                            return;
                        }

                        let merged = normalizeStoredUser(stored);
                        const payload = parseJwtPayload(token);
                        if (
                            payload &&
                            typeof payload.guestId === 'string' &&
                            payload.guestId !== String(stored.userId)
                        ) {
                            merged = { ...merged, userId: payload.guestId };
                        }
                        if (!cancelled) {
                            setUser(merged);
                            secureLocalStorage.setItem('user', merged);
                        }
                        return;
                    }

                    removeAuthToken();
                    secureLocalStorage.removeItem('user');
                    await obtainGuestSession();
                    return;
                }

                // --- Token but no stored user (e.g. cleared "user" key) ---
                if (token) {
                    if (isGuestJwtToken(token)) {
                        try {
                            const { data } = await UserService.refreshGuestSession(token);
                            if (cancelled) return;
                            setAuthToken(data.token);
                            storeUser(authUserFromGuestSession(data));
                        } catch {
                            if (cancelled) return;
                            removeAuthToken();
                            await obtainGuestSession();
                        }
                        return;
                    }

                    if (!isJwtExpired(token)) {
                        if (!cancelled) {
                            window.location.href = '/login';
                        }
                        return;
                    }
                    removeAuthToken();
                    if (!cancelled) {
                        window.location.href = '/login';
                    }
                    return;
                }

                // --- No token: first visit or fully cleared ---
                await obtainGuestSession();
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        init();
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <AuthContext.Provider value={{ user, storeUser, setLoggedInUser, logout, patchUserSettings }}>
            {loading ? <Loading text="Getting your session…" /> : children}
        </AuthContext.Provider>
    );
};

